import { useEffect, useState } from "react";
import WindowSearch from "@/components/WindowSearch";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Loader2, X, Search, Star, Trash2, Check } from "lucide-react";
import { api } from "../lib/api";
import { previewCss, previewBgCss } from "../lib/presets";

const TABS = [
  { key: "all", label: "All" },
  { key: "text", label: "Text" },
  { key: "caption", label: "Captions" },
  { key: "animation", label: "Animations" },
  { key: "my", label: "My Presets" },
];

const FAV_KEY = "cm_preset_favs";
const loadFavs = () => { try { return new Set(JSON.parse(localStorage.getItem(FAV_KEY) || "[]")); } catch { return new Set(); } };

const PresetCard = ({ preset, fav, onFav, onApply, onDelete, disabled }) => {
  const [hover, setHover] = useState(false);
  const def = preset.definition || {};
  const anim = def.animation || "pop";
  const bg = previewBgCss(def);
  return (
    <div data-testid={`preset-card-${preset.id}`} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      className="group relative flex flex-col overflow-hidden rounded-xl border border-white/10 bg-white/[0.02] transition-colors hover:border-[#00F0FF]/50">
      <div className="relative flex h-20 items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_center,#1a1a1a,#0a0a0a)] px-2">
        {bg && <div className="absolute inset-x-3 h-10 rounded-md" style={bg} />}
        <span key={hover ? "h" : "s"} className={`relative z-10 truncate text-lg ${hover ? `cm-anim-${anim}` : ""}`} style={previewCss(def)}>Hello</span>
      </div>
      <div className="flex items-center justify-between gap-1 border-t border-white/10 px-2 py-1.5">
        <div className="min-w-0">
          <div className="truncate text-xs font-semibold text-white">{preset.name}</div>
          <div className="text-[10px] capitalize text-[#666]">{preset.category}{preset.is_builtin ? "" : " · mine"}</div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button data-testid={`preset-fav-${preset.id}`} onClick={() => onFav(preset.id)} title="Favorite" className={`rounded p-1 ${fav ? "text-[#FFD400]" : "text-[#666] hover:text-white"}`}><Star size={13} fill={fav ? "#FFD400" : "none"} /></button>
          {!preset.is_builtin && <button data-testid={`preset-delete-${preset.id}`} onClick={() => onDelete(preset)} title="Delete" className="rounded p-1 text-[#666] hover:text-red-400"><Trash2 size={13} /></button>}
          <button data-testid={`preset-apply-${preset.id}`} onClick={() => onApply(preset)} disabled={disabled} className="rounded-md bg-[#00F0FF] px-2 py-1 text-[11px] font-bold text-black hover:bg-[#00F0FF]/90 disabled:opacity-40">Apply</button>
        </div>
      </div>
    </div>
  );
};

export const PresetsPanel = ({ open, onClose, onApply, selectedCount = 0, totalCaptions = 0 }) => {
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [favs, setFavs] = useState(loadFavs);
  const [mode, setMode] = useState("selected"); // current | selected | all

  const refresh = () => {
    setLoading(true);
    api.listPresets(tab === "all" ? null : tab, q || null)
      .then(setPresets).catch(() => toast.error("Could not load presets.")).finally(() => setLoading(false));
  };
  useEffect(() => { if (open) refresh(); /* eslint-disable-next-line */ }, [open, tab]);
  useEffect(() => { if (!open) return; const t = setTimeout(refresh, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => { setMode(selectedCount > 1 ? "selected" : "current"); }, [selectedCount, open]);

  if (!open) return null;

  const toggleFav = (id) => setFavs((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); try { localStorage.setItem(FAV_KEY, JSON.stringify([...n])); } catch { /* noop */ } return n; });
  const del = async (p) => { if (!window.confirm(`Delete preset "${p.name}"?`)) return; try { await api.deletePreset(p.id); refresh(); toast.success("Preset deleted"); } catch { toast.error("Delete failed"); } };
  const doApply = (preset) => { onApply && onApply(preset, mode); };

  const shown = [...presets].sort((a, b) => (favs.has(b.id) ? 1 : 0) - (favs.has(a.id) ? 1 : 0));

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="presets-panel">
      <div className="relative flex max-h-[85vh] w-full max-w-3xl flex-col rounded-2xl border border-white/10 bg-[#0d0d0d] text-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <WindowSearch />
        <div className="flex items-center justify-between border-b border-white/10 p-4">
          <div className="font-display text-lg font-black">Presets & Templates</div>
          <button data-testid="presets-close" onClick={onClose} className="rounded-lg p-1 text-[#A1A1AA] hover:text-white"><X size={18} /></button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-white/10 p-3">
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5">
            <Search size={14} className="text-[#666]" />
            <input data-testid="presets-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search presets…" className="w-full bg-transparent text-sm outline-none placeholder:text-[#666]" />
          </div>
          <div className="flex gap-1">
            {TABS.map((t) => (
              <button key={t.key} data-testid={`presets-tab-${t.key}`} onClick={() => setTab(t.key)} className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${tab === t.key ? "bg-[#00F0FF] text-black" : "border border-white/10 text-[#A1A1AA] hover:text-white"}`}>{t.label}</button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 border-b border-white/10 bg-white/[0.02] px-3 py-2 text-xs">
          <span className="text-[#A1A1AA]">Apply to:</span>
          {[["current", "Current caption"], ["selected", `Selected (${selectedCount})`], ["all", `All captions (${totalCaptions})`]].map(([m, l]) => (
            <button key={m} data-testid={`presets-mode-${m}`} onClick={() => setMode(m)} className={`flex items-center gap-1 rounded-full px-2.5 py-1 font-semibold ${mode === m ? "bg-[#00F0FF]/15 text-[#00F0FF]" : "text-[#A1A1AA] hover:text-white"}`}>{mode === m && <Check size={12} />}{l}</button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00F0FF]" /></div>
          ) : !shown.length ? (
            <div className="py-16 text-center text-sm text-[#666]">No presets found.</div>
          ) : (
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
              {shown.map((p) => <PresetCard key={p.id} preset={p} fav={favs.has(p.id)} onFav={toggleFav} onApply={doApply} onDelete={del} />)}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PresetsPanel;
